import type { ApiApplyRequest, ApiTaskProposal } from '../api/ai.contract';
import type {
  FieldProvenance,
  ProposalCandidate,
  ProposalField,
  ProposedTagRef,
  QuickTaskProposal,
} from '../types';
import type { QuickTaskDraftFormValues } from '../schemas';
import { priorityFromInt, priorityToInt } from '@/shared/lib/priority';

// ---------------------------------------------------------------------------
// Provenance translation
// ---------------------------------------------------------------------------

const PROVENANCE_MAP: Record<string, FieldProvenance> = {
  'model-suggested': 'model',
  'from-context': 'context',
  'not-supplied': 'notSupplied',
};

function translateProvenance(raw: string): FieldProvenance {
  const mapped = PROVENANCE_MAP[raw];
  if (mapped === undefined) {
    throw new Error(`Unknown provenance value: "${raw}"`);
  }
  return mapped;
}

// ---------------------------------------------------------------------------
// fromApiProposal — wire → view model
// ---------------------------------------------------------------------------

/**
 * Converts an `ApiTaskProposal` (backend wire format) into the internal
 * `QuickTaskProposal` view model consumed by UI components.
 */
export function fromApiProposal(dto: ApiTaskProposal): QuickTaskProposal {
  const correlationId = dto.correlationId ?? '';

  // Title
  const title: ProposalField = {
    value: dto.title.value,
    provenance: translateProvenance(dto.title.provenance),
  };

  // Description
  const description: ProposalField = {
    value: dto.description.value,
    provenance: translateProvenance(dto.description.provenance),
  };

  // Status — when id is null and name is set, value is the name (new-column case)
  const status: ProposalField = {
    value: dto.status.id ?? dto.status.name,
    provenance: translateProvenance(dto.status.provenance),
    isNew: dto.status.isNew,
    candidates: dto.status.candidates.map((c): ProposalCandidate => ({ id: c.id, name: c.name })),
    color: dto.status.color ?? null,
  };

  // Assignee — only a real member id is a valid assigneeId. A plain-text name
  // must never be sent as an id to the backend. When both id and name are null
  // AND provenance is 'not-supplied' (true non-member), clear candidates.
  // When provenance is model/context with null id+name, it's an ambiguous
  // pick — keep candidates so the user can choose the real id.
  const assigneeIsNull = dto.assignee.id === null && dto.assignee.name === null;
  const assigneeIsNonMember = assigneeIsNull && dto.assignee.provenance === 'not-supplied';
  const assignee: ProposalField = {
    value: dto.assignee.id,
    provenance: translateProvenance(dto.assignee.provenance),
    isNew: dto.assignee.isNew,
    candidates: assigneeIsNonMember
      ? []
      : dto.assignee.candidates.map((c): ProposalCandidate => ({ id: c.id, name: c.name })),
  };

  // Group — same rule as status
  const group: ProposalField = {
    value: dto.group.id ?? dto.group.name,
    provenance: translateProvenance(dto.group.provenance),
    isNew: dto.group.isNew,
    candidates: dto.group.candidates.map((c): ProposalCandidate => ({ id: c.id, name: c.name })),
  };

  // Priority
  const priority: ProposalField = {
    value: priorityFromInt(dto.priority.value) ?? null,
    provenance: translateProvenance(dto.priority.provenance),
  };

  // Estimated duration — minutes, kept as a string like every other proposal value so the
  // view model stays one shape; the form parses it back.
  const estimatedMinutes: ProposalField = {
    value: dto.estimatedMinutes?.value != null ? String(dto.estimatedMinutes.value) : null,
    provenance: dto.estimatedMinutes
      ? translateProvenance(dto.estimatedMinutes.provenance)
      : 'notSupplied',
  };

  // Due date — date only (yyyy-MM-dd)
  const dueDate: ProposalField = {
    value: dto.dueDate.value?.date ?? null,
    provenance: translateProvenance(dto.dueDate.provenance),
  };

  // Start date — same shape as due date
  const startDate: ProposalField = {
    value: dto.startDate?.value?.date ?? null,
    provenance: dto.startDate ? translateProvenance(dto.startDate.provenance) : 'notSupplied',
  };

  // Tags — every entry. Keeping only the first dropped whatever the model proposed after it,
  // so an instruction naming three tags confirmed with one.
  // `||` and not `??`: an id or name arriving as an empty string is not a value. `??` only
  // guards null and undefined, so `{ id: '', name: 'Web' }` failed the check and the tag was
  // dropped outright.
  const tagRefs: ProposedTagRef[] = [];
  let firstUsableTag: ApiTaskProposal['tags'][number] | null = null;
  for (const tag of dto.tags) {
    if (!tag.id && !tag.name) continue;
    firstUsableTag ??= tag;
    tagRefs.push({
      id: tag.id || null,
      name: tag.name ?? '',
      isNew: tag.isNew,
      color: tag.color ?? null,
    });
  }

  // The chip's summary of that list: `isNew` marks the field when *any* entry is new, since
  // the warning it drives is per-name and rendered from the list itself.
  const tags: ProposalField = tagRefs.length
    ? {
        value: tagRefs[0]!.id || tagRefs[0]!.name,
        // From the entry that survived the filter, not from `dto.tags[0]`: a dropped first entry
        // was still supplying the provenance for the one the chip actually shows.
        provenance: translateProvenance(firstUsableTag!.provenance),
        isNew: tagRefs.some((tag) => tag.isNew),
      }
    : { value: null, provenance: 'notSupplied' };

  // Observations — pass through
  const observations = dto.observations;

  // NOTE: `dto.newEntities` and `dto.ambiguities` are intentionally not mapped
  // into the view model. New-entity warnings are derived from `isNew` flags on
  // individual fields (status, assignee, group, tags). `ambiguities` is a
  // backend diagnostic — candidates already arrive on entity references via
  // ApiProposalEntityReference.

  return {
    correlationId,
    tags: tagRefs,
    fields: {
      title,
      description,
      statusId: status,
      assigneeId: assignee,
      groupId: group,
      priority,
      estimatedMinutes,
      startDate,
      dueDate,
      tags,
    },
    observations,
  };
}

// ---------------------------------------------------------------------------
// toApplyRequest — view model → wire
// ---------------------------------------------------------------------------

/**
 * Converts a user-approved draft + its originating proposal into the
 * `ApiApplyRequest` wire payload sent to the backend.
 */
export function toApplyRequest(
  draft: QuickTaskDraftFormValues,
  proposal: QuickTaskProposal,
  callerTimeZoneId: string,
  isEdited: boolean,
): ApiApplyRequest {
  const { fields } = proposal;

  // Status and group — the proposal says whether it *named* something new, the draft says
  // whether that is still what the user is confirming. Deciding from the proposal alone meant a
  // user who opened the chip and picked an existing column still created the proposed one: the
  // id they chose was discarded and the name was sent, adding a column to the whole board
  // against the correction they had just made.
  //
  // A value still to be created is held in the draft by name, which is what
  // `proposalToFormValues` puts there — so equality with the proposed name is what tells the two
  // apart, exactly as it does for tags below.
  const proposedNewStatus = fields.statusId.isNew === true ? fields.statusId.value : null;
  const statusIsNew = proposedNewStatus !== null && draft.statusId === proposedNewStatus;
  const statusId = statusIsNew ? '' : (draft.statusId ?? '');
  const statusName = statusIsNew ? proposedNewStatus : null;
  const statusColor = statusIsNew ? (fields.statusId.color ?? null) : null;

  const proposedNewGroup = fields.groupId?.isNew === true ? fields.groupId.value : null;
  const groupIsNew = proposedNewGroup !== null && draft.groupId === proposedNewGroup;
  const groupId = groupIsNew ? '' : (draft.groupId ?? null);
  const groupName = groupIsNew ? proposedNewGroup : null;

  // Assignee
  const assigneeId = draft.assigneeId ?? null;

  // Priority — convert string enum to backend int
  const priority = priorityToInt(draft.priority);

  // Due date — draft already holds yyyy-MM-dd or null
  const dueDate = draft.dueDate ?? null;

  // Start date — draft already holds yyyy-MM-dd or null
  const startDate = draft.startDate ?? null;

  // Description — null if undefined
  const description = draft.description ?? null;

  // Tags — decided per entry, not for the list as a whole. Only a name the proposal itself
  // marked new is sent as a name; everything else is an id the user picked. Treating the whole
  // list as names once the proposal had a new tag sent the id of any tag the user then chose as
  // its name, which created a tag literally named after an id.
  //
  // One map, not a set beside it: membership answers "is this a name to create?" and the value
  // answers "in what colour", so two structures over the same predicate meant walking the tags
  // four times to learn one thing.
  const proposedNewTagColors = new Map<string, string | null>();
  for (const tag of proposal.tags) {
    if (tag.isNew) proposedNewTagColors.set(tag.name, tag.color);
  }
  const tags: ApiApplyRequest['tags'] = (draft.tags ?? []).map((entry) =>
    proposedNewTagColors.has(entry)
      ? { id: null, name: entry, isNew: true, color: proposedNewTagColors.get(entry) ?? null }
      : { id: entry, name: null, isNew: false },
  );

  return {
    title: draft.title,
    description,
    statusId,
    statusName,
    statusColor,
    groupId,
    groupName,
    assigneeId,
    priority,
    estimatedMinutes: draft.estimatedMinutes ?? null,
    startDate,
    dueDate,
    tags,
    correlationId: proposal.correlationId,
    isEdited,
    callerTimeZoneId,
  };
}
